import classNames from "classnames";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { FC, useEffect, useRef } from "react";
import { getLeagueLogoUrl } from "../../data/leagues";
import { API } from "../../services/api/types";
import { getDynamicMobileTagId } from "../../utils/ads";
import { humanizeDate } from "../../utils/date";
import { createImageSizes } from "../../utils/image";
import {
  getGamesTitleByCampaignId,
  getGamesTitleByCategoryId,
} from "../../utils/sponsoredOdds";
import Advertisement, { AdvertisementProps } from "../Advertisement";
import ArticleFooter from "../ArticleItem/ArticleFooter";
import Games from "../Games";
import SectionHeader from "../SectionHeader";
import ArticleShare from "./ArticleShare";
import { getArticleSegments, resolveHtml } from "./lib";

export type ArticleAdConfig = {
  text: {
    start: number;
    end: number;
    between: number;
    adProps: AdvertisementProps;
  } | null;
  preamble: {
    adProps: AdvertisementProps;
    position: "before" | "after";
  } | null;
  social: {
    adProps: AdvertisementProps;
    position: "before" | "after";
  } | null;
  videoIntelligence: {
    adProps: AdvertisementProps;
    position: number;
    end: number;
  } | null;
};

type ArticleProps = {
  article: API.Article;
  tags: API.ArticleTag[];
  campaign?: API.Campaign;
  adConfig: ArticleAdConfig;
  titlePosition: "TOP" | "UNDER_IMAGE";
  games?: API.Game[];
};

const Article: FC<ArticleProps> = (props) => {
  const router = useRouter();
  const bodyRef = useRef(null);

  const articleTextSegments = getArticleSegments(props.article.text);

  const leagueLogoUrl = props.article.leagueLogo
    ? `${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${props.article.leagueLogo}`
    : getLeagueLogoUrl(props.article.categoriesId) || null;

  const loadEmbeds = () => {
    (window as any).twttr?.widgets?.load?.();
    (window as any).instgrm?.Embeds?.process?.();
  };

  useEffect(() => {
    [1, 1000, 5000, 10000].forEach((delay) => {
      setTimeout(loadEmbeds, delay);
    });
  }, []);

  useEffect(() => {
    // making sure we navigate with next when it is an internal link
    (bodyRef.current as any).querySelectorAll("a").forEach((a: any) => {
      a.addEventListener("click", (event: any) => {
        event.preventDefault();
        const href = a.getAttribute("href");
        const isExternal = href.includes("http");
        if (isExternal) return window.open(href, "_blank");
        router.push(href);
      });
    });
  }, []);

  useEffect(() => {
    const containers = document.querySelectorAll(".viralize-widget");

    containers.forEach((container) => {
      if (container.getAttribute("data-script-loaded")) return;

      const zid = container.getAttribute("data-zid");
      const cid = container.getAttribute("data-cid");

      if (!zid) return;
      // cid is optional

      const script = document.createElement("script");
      script.setAttribute("data-wid", "auto");
      script.type = "text/javascript";
      script.async = true;
      script.src = `https://content.viralize.tv/display/?zid=${encodeURIComponent(
        zid
      )}&cid=${encodeURIComponent(cid!)}`;

      container.appendChild(script);
      container.setAttribute("data-script-loaded", "true");
    });
  }, []);

  const renderArticleTextSegment =
    (segmentLength: number, campaign?: API.Campaign) =>
    (segment: string, index: number) => {
      const isFirst = index === 0;
      const hasTextConfig = props.adConfig.text !== null;
      const hasReachedEndMargin = !(
        index + 1 <=
        segmentLength - (props.adConfig.text?.end || 0)
      );
      const hasReachedEndMarginVI = !(
        index + 1 <=
        segmentLength - (props.adConfig.videoIntelligence?.end || 0)
      );
      const hasPassedStartMargin =
        index + 1 > (props.adConfig.text?.start || 0);
      const shouldRenderVideoIntelligence =
        !hasReachedEndMarginVI &&
        props.adConfig.videoIntelligence?.position === index + 1 &&
        !props.article.hideVideo;
      const shouldRenderFirstAd =
        hasTextConfig &&
        (props.adConfig.text?.start || 0) === index + 1 &&
        !hasReachedEndMargin;
      const shouldRenderContinousAd =
        hasTextConfig &&
        !isFirst &&
        hasPassedStartMargin &&
        !hasReachedEndMargin &&
        (index + 1 - (props.adConfig.text?.start || 0)) %
          (props.adConfig.text?.between || 0) ===
          0;

      return (
        <React.Fragment key={index}>
          <div
            className={classNames("text-xl", "article-text")}
            dangerouslySetInnerHTML={{ __html: resolveHtml(segment) }}
          />

          {shouldRenderFirstAd && (
            <>
              <div className={classNames("mt-5")} />
              <Advertisement
                {...props.adConfig.text!.adProps}
                codeReplaceAll={() => ({ tagId: getDynamicMobileTagId() })}
              />
              <div className={classNames("mb-5")} />
            </>
          )}

          {shouldRenderContinousAd && (
            <>
              <Advertisement
                {...props.adConfig.text!.adProps}
                codeReplaceAll={() => ({ tagId: getDynamicMobileTagId() })}
              />
              <div className={classNames("mb-5")} />
            </>
          )}

          {shouldRenderVideoIntelligence && (
            <>
              <Advertisement {...props.adConfig.videoIntelligence!.adProps} />
              <div className={classNames("mb-5")} />
            </>
          )}
        </React.Fragment>
      );
    };

  return (
    <>
      {props.titlePosition === "TOP" && (
        <>
          <h1
            className={classNames("text-4xl", "font-bold", "text-text-primary")}
          >
            {props.article.title}
          </h1>
          <div className={classNames("mb-3")} />
          <ArticleFooter
            leaugeIcon={leagueLogoUrl}
            leagueName={props.article.categoriesName}
            date={humanizeDate(props.article.date)}
          />
          <div className={classNames("mb-5")} />
        </>
      )}
      {props.article.imageLarge && (
        <>
          {Boolean(props.article.sponsored) && (
            <SectionHeader smallText label="Sponsrad artikel" hideLeftBorder />
          )}
          {Boolean(props.article.scoop) && (
            <SectionHeader
              smallText
              label="Fotbolltransfers.com avslöjar"
              hideLeftBorder
            />
          )}
          <div className="relative aspect-w-16 aspect-h-9">
            <Image
              layout="fill"
              sizes={createImageSizes({
                mobile: "100vw",
                tablet: "50vw",
                desktop: "75vw",
              })}
              src={`${process.env.NEXT_PUBLIC_ASSET_SERVER_URL}${props.article.imageLarge}`}
              alt="Artikelbild" // TODO: Get image description from DB
              className="object-cover"
            />
          </div>
          <div className={classNames("flex", "justify-end", "mt-1", "mb-3")}>
            <p className={classNames("text-sm", "text-text-secondary")}>
              {props.article.imagesource}
            </p>
          </div>
        </>
      )}
      {props.titlePosition === "UNDER_IMAGE" && (
        <>
          <h1
            className={classNames("text-4xl", "font-bold", "text-text-primary")}
          >
            {props.article.title}
          </h1>
          <div className={classNames("mt-5")} />
          <ArticleFooter
            leaugeIcon={leagueLogoUrl}
            leagueName={props.article.categoriesName}
            date={humanizeDate(props.article.date)}
          />
        </>
      )}
      {props.article.id === 93473 && (
        <button
          className={classNames("hover:underline", "mt-5")}
          onClick={() => {
            if (typeof window !== "undefined") {
              (window as any)?.__tcfapi(
                "displayConsentUi",
                2,
                function () {}
              ) as any;
            }
          }}
        >
          Ändra samtycke
        </button>
      )}
      {["both", "before"].includes(props.adConfig.preamble?.position || "") && (
        <>
          <div className={classNames("mt-5")} />
          <Advertisement {...props.adConfig.preamble!.adProps} />
        </>
      )}
      <p className={classNames("mt-5", "text-[1.3rem]", "font-semibold")}>
        {props.article.preamble}
      </p>

      {!props.article.sponsored && props.article.categoriesId !== 24 && Boolean(props.games?.length) && (
        <div className="mt-5">
          <Games
            games={props.games!}
            title={
              getGamesTitleByCampaignId(
                props.games?.length!,
                props.campaign?.id!
              ) ||
              getGamesTitleByCategoryId(
                props.games?.length!,
                props.article.categoriesId
              )
            }
            campaign={props.campaign}
          />
        </div>
      )}

      {/* <ArticleGames categoryId={props.article.categoriesId} categoryName={props.article.categoriesName} /> */}

      {["both", "after"].includes(props.adConfig.preamble?.position || "") && (
        <>
          <div className={classNames("mt-5")} />
          <Advertisement {...props.adConfig.preamble!.adProps} />
        </>
      )}
      <div ref={bodyRef} className={classNames("mt-5")}>
        {articleTextSegments!.map(
          renderArticleTextSegment(articleTextSegments!.length, props.campaign)
        )}
      </div>
      {["both", "before"].includes(props.adConfig.social?.position || "") && (
        <>
          <Advertisement {...props.adConfig.social!.adProps} />
          <div className={classNames("mb-5")} />
        </>
      )}
      <ArticleShare isSponsored={props.article.categoriesId === 24} />
      {["both", "after"].includes(props.adConfig.social?.position || "") && (
        <>
          <Advertisement {...props.adConfig.social!.adProps} />
        </>
      )}
      <div className={classNames("mt-5")} />
      <p className={classNames("font-semibold")}>{props.article.writerName}</p>
      <p>{props.article.writerEmail}</p>
      {props.article.categoriesId === 24 && <div className="mb-10" />}
    </>
  );
};

export default React.memo(Article);
